import { useId, type ChangeEvent } from 'react';
import { COMPONENT_PAGE_COPY } from './component-page-copy';
import styles from './TextField.module.css';

interface TextFieldProps {
  label: string;
  help?: string;
  placeholder?: string;
  value: string;
  maxLength?: number;
  onChange: (value: string) => void;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  showCount?: boolean;
}

/** A labelled text input or text area with optional help text and a character count. */
export const TextField = ({
  label,
  help,
  placeholder,
  value,
  maxLength,
  onChange,
  required = false,
  multiline = false,
  rows = 3,
  showCount = false,
}: TextFieldProps) => {
  const inputId = useId();
  const helpId = useId();
  const describedBy = help ? helpId : undefined;
  const fieldHasCharacterLimit = maxLength !== undefined;
  const characterCountIsShown = showCount && fieldHasCharacterLimit;
  const characterCountLabel = fieldHasCharacterLimit
    ? COMPONENT_PAGE_COPY.characterCount(value.length, maxLength)
    : undefined;
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange(event.target.value);

  return (
    <div className={styles.field}>
      <label htmlFor={inputId}>{label}</label>
      {help && <p id={helpId}>{help}</p>}
      {multiline ? (
        <textarea
          id={inputId}
          value={value}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          required={required}
          aria-describedby={describedBy}
          onChange={handleChange}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          required={required}
          aria-describedby={describedBy}
          onChange={handleChange}
        />
      )}
      {characterCountIsShown && <p className={styles.count}>{characterCountLabel}</p>}
    </div>
  );
};
